import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  ArrowLeft, 
  Plus, 
  Edit, 
  Trash2, 
  Upload,
  BarChart3,
  Sparkles,
  FolderOpen,
  FileText,
  Users,
  ChevronRight,
  ChevronDown,
  Save,
  X,
  Play
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const apiClient = axios.create({ baseURL: API });
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const PerlasAdmin = ({ user, onLogout }) => {
  const navigate = useNavigate();
  
  // States
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');
  const [modules, setModules] = useState([]);
  const [subtopics, setSubtopics] = useState([]);
  const [pearls, setPearls] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [expandedModule, setExpandedModule] = useState(null);
  
  // Form states
  const [showPearlForm, setShowPearlForm] = useState(false);
  const [editingPearl, setEditingPearl] = useState(null);
  const [pearlForm, setPearlForm] = useState({
    subtopic_id: '',
    title: '',
    message: '',
    long_body: '',
    tags: '',
    difficulty: '',
    priority: 0
  });
  
  // CSV upload
  const [csvFile, setCsvFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  // Pearl list filters
  const [filterModule, setFilterModule] = useState('');
  const [filterSubtopic, setFilterSubtopic] = useState('');
  const [filterSubtopicOptions, setFilterSubtopicOptions] = useState([]);
  
  // Quiz categories for import
  const [quizCategories, setQuizCategories] = useState([]);
  const [selectedSpecialty, setSelectedSpecialty] = useState('');
  const [selectedTopic, setSelectedTopic] = useState('');
  const [availableTopics, setAvailableTopics] = useState([]);

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/');
      return;
    }
    loadData();
  }, [user, navigate]);

  // Update available topics when specialty changes (for CSV import)
  useEffect(() => {
    if (selectedSpecialty && quizCategories.length > 0) {
      const category = quizCategories.find(c => c.module === selectedSpecialty);
      setAvailableTopics(category?.subtemas || []);
      setSelectedTopic('');
    } else {
      setAvailableTopics([]);
      setSelectedTopic('');
    }
  }, [selectedSpecialty, quizCategories]);

  // Update filter subtopic options when filter module changes (for pearl list)
  useEffect(() => {
    if (filterModule && quizCategories.length > 0) {
      const category = quizCategories.find(c => c.module === filterModule);
      setFilterSubtopicOptions(category?.subtemas || []);
      setFilterSubtopic('');
    } else {
      setFilterSubtopicOptions([]);
      setFilterSubtopic('');
    }
  }, [filterModule, quizCategories]);

  // Get filtered pearls
  const filteredPearls = pearls.filter(pearl => {
    if (filterModule && pearl.module_name !== filterModule) return false;
    if (filterSubtopic && pearl.subtopic_name !== filterSubtopic) return false;
    return true;
  });

  const loadData = async () => {
    try {
      setLoading(true);
      const [modulesRes, pearlsRes, analyticsRes, categoriesRes] = await Promise.all([
        apiClient.get('/perlas/all-subtopics'),
        apiClient.get('/admin/perlas/all'),
        apiClient.get('/admin/perlas/analytics'),
        apiClient.get('/admin/perlas/quiz-categories')
      ]);
      
      // Build modules with subtopics
      const modulesWithSubtopics = modulesRes.data;
      setModules(modulesWithSubtopics);
      
      // Flatten subtopics for form select
      const allSubtopics = [];
      modulesWithSubtopics.forEach(m => {
        m.subtopics.forEach(s => {
          allSubtopics.push({
            ...s,
            module_name: m.module.name
          });
        });
      });
      setSubtopics(allSubtopics);
      
      setPearls(pearlsRes.data);
      setAnalytics(analyticsRes.data);
      setQuizCategories(categoriesRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePearl = async () => {
    if (!pearlForm.subtopic_id || !pearlForm.title || !pearlForm.message) {
      toast.error('Completa los campos requeridos');
      return;
    }
    
    try {
      const data = {
        ...pearlForm,
        tags: pearlForm.tags ? pearlForm.tags.split(',').map(t => t.trim()) : [],
        priority: parseInt(pearlForm.priority) || 0
      };
      
      if (editingPearl) {
        await apiClient.put(`/admin/perlas/pearls/${editingPearl.id}`, data);
        toast.success('Perla actualizada');
      } else {
        await apiClient.post('/admin/perlas/pearls', data);
        toast.success('Perla creada');
      }
      
      setShowPearlForm(false);
      setEditingPearl(null);
      resetPearlForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar perla');
    }
  };

  const handleDeletePearl = async (pearlId) => {
    if (!window.confirm('¿Eliminar esta perla?')) return;
    
    try {
      await apiClient.delete(`/admin/perlas/pearls/${pearlId}`);
      toast.success('Perla eliminada');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleEditPearl = (pearl) => {
    setEditingPearl(pearl);
    setPearlForm({
      subtopic_id: pearl.subtopic_id,
      title: pearl.title,
      message: pearl.message,
      long_body: pearl.long_body || '',
      tags: pearl.tags?.join(', ') || '',
      difficulty: pearl.difficulty || '',
      priority: pearl.priority || 0
    });
    setShowPearlForm(true);
  };

  const resetPearlForm = () => {
    setPearlForm({
      subtopic_id: '',
      title: '',
      message: '',
      long_body: '',
      tags: '',
      difficulty: '',
      priority: 0
    });
  };

  const handleCsvUpload = async () => {
    if (!csvFile) {
      toast.error('Selecciona un archivo CSV');
      return;
    }
    
    if (!selectedSpecialty) {
      toast.error('Selecciona un módulo');
      return;
    }
    
    // Check if module requires subtema
    const category = quizCategories.find(c => c.module === selectedSpecialty);
    if (category?.subtemas.length > 0 && !selectedTopic) {
      toast.error('Selecciona un subtema');
      return;
    }
    
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', csvFile);
      formData.append('specialty', selectedSpecialty);
      if (selectedTopic) {
        formData.append('topic', selectedTopic);
      }
      
      const res = await apiClient.post('/admin/perlas/bulk-import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(res.data.message);
      if (res.data.errors?.length > 0) {
        console.warn('Import errors:', res.data.errors);
      }
      
      setCsvFile(null);
      setSelectedSpecialty('');
      setSelectedTopic('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al importar');
    } finally {
      setUploading(false);
    }
  };

  if (!user?.is_admin) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <button
              onClick={() => navigate('/admin')}
              className="mr-4 p-2 rounded-full hover:bg-gray-100"
            >
              <ArrowLeft size={20} />
            </button>
            <h1 className="text-xl font-bold text-gray-800 flex items-center gap-2">
              <Sparkles className="text-amber-500" size={24} />
              Panel de Perlas Diarias
            </h1>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-sm p-4 sticky top-24">
                <nav className="space-y-2">
                  <button
                    onClick={() => setActiveSection('overview')}
                    className={`w-full flex items-center gap-2 px-4 py-2 rounded-lg transition ${
                      activeSection === 'overview' 
                        ? 'bg-amber-100 text-amber-700' 
                        : 'hover:bg-gray-100'
                    }`}
                  >
                    <BarChart3 size={18} />
                    Resumen
                  </button>
                  <button
                    onClick={() => setActiveSection('modules')}
                    className={`w-full flex items-center gap-2 px-4 py-2 rounded-lg transition ${
                      activeSection === 'modules' 
                        ? 'bg-amber-100 text-amber-700' 
                        : 'hover:bg-gray-100'
                    }`}
                  >
                    <FolderOpen size={18} />
                    Módulos y Subtemas
                  </button>
                  <button
                    onClick={() => setActiveSection('pearls')}
                    className={`w-full flex items-center gap-2 px-4 py-2 rounded-lg transition ${
                      activeSection === 'pearls' 
                        ? 'bg-amber-100 text-amber-700' 
                        : 'hover:bg-gray-100'
                    }`}
                  >
                    <FileText size={18} />
                    Perlas ({pearls.length})
                  </button>
                  <button
                    onClick={() => setActiveSection('import')}
                    className={`w-full flex items-center gap-2 px-4 py-2 rounded-lg transition ${
                      activeSection === 'import' 
                        ? 'bg-amber-100 text-amber-700' 
                        : 'hover:bg-gray-100'
                    }`}
                  >
                    <Upload size={18} />
                    Importar CSV
                  </button>
                </nav>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3">
              {/* Overview Section */}
              {activeSection === 'overview' && analytics && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h2 className="text-2xl font-bold text-gray-800">Resumen</h2>
                    <button
                      onClick={async () => {
                        try {
                          await apiClient.post('/admin/perlas/trigger-scheduler');
                          toast.success('Scheduler ejecutado manualmente');
                          loadData();
                        } catch (error) {
                          toast.error('Error al ejecutar scheduler');
                        }
                      }}
                      className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg text-sm"
                    >
                      <Play size={16} />
                      Ejecutar Scheduler Ahora
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-white rounded-xl shadow-sm p-6">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                          <Users className="text-amber-600" size={24} />
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Usuarios Suscritos</p>
                          <p className="text-2xl font-bold text-gray-800">
                            {analytics.total_subscribed_users}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-white rounded-xl shadow-sm p-6">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                          <Sparkles className="text-amber-600" size={24} />
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Total Perlas</p>
                          <p className="text-2xl font-bold text-gray-800">
                            {analytics.total_pearls}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-white rounded-xl shadow-sm p-6">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                          <BarChart3 className="text-green-600" size={24} />
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Tasa de Clic</p>
                          <p className="text-2xl font-bold text-gray-800">
                            {analytics.delivery_stats?.click_rate || 0}%
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Subscriptions by Module */}
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <h3 className="text-lg font-semibold mb-4">Suscripciones por Módulo</h3>
                    {analytics.subscriptions_by_module?.length > 0 ? (
                      <div className="space-y-3">
                        {analytics.subscriptions_by_module.map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between">
                            <span className="text-gray-700">{item.module_name}</span>
                            <span className="font-semibold text-amber-600">{item.count} usuarios</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500">No hay suscripciones aún</p>
                    )}
                  </div>

                  {/* Delivery Stats */}
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <h3 className="text-lg font-semibold mb-4">Estadísticas de Entrega</h3>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-center p-4 bg-green-50 rounded-lg">
                        <p className="text-3xl font-bold text-green-600">
                          {analytics.delivery_stats?.delivered || 0}
                        </p>
                        <p className="text-sm text-green-700">Entregadas</p>
                      </div>
                      <div className="text-center p-4 bg-red-50 rounded-lg">
                        <p className="text-3xl font-bold text-red-600">
                          {analytics.delivery_stats?.failed || 0}
                        </p>
                        <p className="text-sm text-red-700">Fallidas</p>
                      </div>
                      <div className="text-center p-4 bg-blue-50 rounded-lg">
                        <p className="text-3xl font-bold text-blue-600">
                          {analytics.delivery_stats?.clicked || 0}
                        </p>
                        <p className="text-sm text-blue-700">Clics</p>
                      </div>
                    </div>
                  </div>

                  {/* Scheduler Info */}
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <h3 className="font-semibold text-amber-800 mb-2">ℹ️ Información del Scheduler</h3>
                    <p className="text-amber-700 text-sm">
                      El scheduler se ejecuta automáticamente cada <strong>15 minutos</strong> y envía 
                      perlas a los usuarios suscritos dentro de su horario configurado.
                      La probabilidad de envío se calcula para distribuir las {'{'}frecuencia{'}'} perlas/día 
                      uniformemente en el rango horario del usuario.
                    </p>
                  </div>
                </div>
              )}

              {/* Modules Section */}
              {activeSection === 'modules' && (
                <div className="space-y-4">
                  <h2 className="text-2xl font-bold text-gray-800">Módulos y Subtemas</h2>
                  
                  {modules.map((item) => (
                    <div key={item.module.id} className="bg-white rounded-xl shadow-sm overflow-hidden">
                      <button
                        onClick={() => setExpandedModule(
                          expandedModule === item.module.id ? null : item.module.id
                        )}
                        className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50"
                      >
                        <div className="flex items-center gap-3">
                          <FolderOpen className="text-amber-500" size={20} />
                          <span className="font-semibold text-gray-800">{item.module.name}</span>
                          <span className="text-sm text-gray-500">
                            ({item.subtopics.length} subtemas)
                          </span>
                        </div>
                        {expandedModule === item.module.id ? (
                          <ChevronDown size={20} />
                        ) : (
                          <ChevronRight size={20} />
                        )}
                      </button>
                      
                      {expandedModule === item.module.id && (
                        <div className="border-t bg-gray-50 px-6 py-4">
                          <div className="space-y-2">
                            {item.subtopics.map((subtopic) => (
                              <div 
                                key={subtopic.id}
                                className="flex items-center justify-between py-2 px-4 bg-white rounded-lg"
                              >
                                <span className="text-gray-700">{subtopic.name}</span>
                                <span className="text-sm text-gray-500">
                                  {pearls.filter(p => p.subtopic_id === subtopic.id).length} perlas
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Pearls Section */}
              {activeSection === 'pearls' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h2 className="text-2xl font-bold text-gray-800">Perlas</h2>
                    <button
                      onClick={() => {
                        setEditingPearl(null);
                        resetPearlForm();
                        setShowPearlForm(true);
                      }}
                      className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg"
                    >
                      <Plus size={18} />
                      Nueva Perla
                    </button>
                  </div>
                  
                  {/* Filtros por Módulo y Subtema */}
                  <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                      {/* Filtro Módulo */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Filtrar por Módulo
                        </label>
                        <select
                          value={filterModule}
                          onChange={(e) => setFilterModule(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                        >
                          <option value="">Todos los módulos</option>
                          {quizCategories.map((cat) => (
                            <option key={cat.module} value={cat.module}>
                              {cat.module}
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      {/* Filtro Subtema */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Filtrar por Subtema
                        </label>
                        <select
                          value={filterSubtopic}
                          onChange={(e) => setFilterSubtopic(e.target.value)}
                          disabled={!filterModule || filterSubtopicOptions.length === 0}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm disabled:bg-gray-100"
                        >
                          <option value="">{filterSubtopicOptions.length === 0 ? 'Sin subtemas' : 'Todos los subtemas'}</option>
                          {filterSubtopicOptions.map((subtema) => (
                            <option key={subtema} value={subtema}>
                              {subtema}
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      {/* Contador y botón limpiar */}
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">
                          {filteredPearls.length} de {pearls.length} perlas
                        </span>
                        {(filterModule || filterSubtopic) && (
                          <button
                            onClick={() => {
                              setFilterModule('');
                              setFilterSubtopic('');
                            }}
                            className="text-sm text-amber-600 hover:text-amber-700 font-medium"
                          >
                            Limpiar filtros
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Pearls List */}
                  <div className="space-y-3">
                    {filteredPearls.length === 0 ? (
                      <div className="bg-gray-50 rounded-xl p-8 text-center">
                        <p className="text-gray-500">
                          {pearls.length === 0 
                            ? 'No hay perlas creadas aún' 
                            : 'No hay perlas que coincidan con los filtros seleccionados'}
                        </p>
                      </div>
                    ) : (
                      filteredPearls.map((pearl) => (
                        <div 
                          key={pearl.id}
                          className="bg-white rounded-xl shadow-sm p-4"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                                <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded">
                                  {pearl.module_name || 'Sin módulo'}
                                </span>
                                <ChevronRight size={14} />
                                <span>{pearl.subtopic_name || 'Sin subtema'}</span>
                              </div>
                              <h4 className="font-semibold text-gray-800">{pearl.title}</h4>
                              <p className="text-gray-600 text-sm mt-1">{pearl.message}</p>
                              {pearl.tags?.length > 0 && (
                                <div className="flex gap-1 mt-2">
                                  {pearl.tags.map((tag, idx) => (
                                    <span 
                                      key={idx}
                                      className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded"
                                    >
                                      {tag}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div className="flex gap-2 ml-4">
                              <button
                                onClick={() => handleEditPearl(pearl)}
                                className="p-2 text-gray-500 hover:text-amber-600 hover:bg-amber-50 rounded-lg"
                              >
                                <Edit size={16} />
                              </button>
                              <button
                                onClick={() => handleDeletePearl(pearl.id)}
                                className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {/* Import Section */}
              {activeSection === 'import' && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-800">Importar Perlas desde CSV</h2>
                  
                  {/* Límite de perlas */}
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <p className="text-amber-800 font-medium">
                      ⚠️ Máximo 20 perlas por archivo CSV
                    </p>
                  </div>

                  {/* Paso 1: Seleccionar Módulo y Subtema */}
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <h3 className="font-semibold text-gray-800 mb-4">
                      <span className="bg-amber-500 text-white rounded-full w-6 h-6 inline-flex items-center justify-center text-sm mr-2">1</span>
                      Selecciona Módulo y Subtema
                    </h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Módulo Select */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Módulo
                        </label>
                        <select
                          value={selectedSpecialty}
                          onChange={(e) => setSelectedSpecialty(e.target.value)}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                        >
                          <option value="">Selecciona un módulo...</option>
                          {quizCategories.map((cat) => (
                            <option key={cat.module} value={cat.module}>
                              {cat.module} {cat.subtemas.length > 0 ? `(${cat.subtemas.length} subtemas)` : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      {/* Subtema Select */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Subtema
                        </label>
                        <select
                          value={selectedTopic}
                          onChange={(e) => setSelectedTopic(e.target.value)}
                          disabled={!selectedSpecialty || availableTopics.length === 0}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent disabled:bg-gray-100"
                        >
                          <option value="">{availableTopics.length === 0 ? 'Sin subtemas (solo módulo)' : 'Selecciona un subtema...'}</option>
                          {availableTopics.map((subtema) => (
                            <option key={subtema} value={subtema}>
                              {subtema}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    
                    {selectedSpecialty && (
                      <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-green-800 text-sm">
                          ✓ Las perlas se agregarán a: <strong>{selectedSpecialty}</strong>{selectedTopic ? ` → ${selectedTopic}` : ''}
                        </p>
                      </div>
                    )}
                  </div>
                  
                  {/* Paso 2: Formato del CSV */}
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <h3 className="font-semibold text-gray-800 mb-4">
                      <span className="bg-amber-500 text-white rounded-full w-6 h-6 inline-flex items-center justify-center text-sm mr-2">2</span>
                      Formato del Archivo CSV
                    </h3>
                    
                    <div className="bg-gray-50 rounded-lg p-4 overflow-x-auto mb-4">
                      <table className="text-sm w-full">
                        <thead>
                          <tr className="text-left text-gray-500 border-b">
                            <th className="pr-4 pb-2">Columna</th>
                            <th className="pr-4 pb-2">Requerida</th>
                            <th className="pb-2">Descripción</th>
                          </tr>
                        </thead>
                        <tbody className="text-gray-700">
                          <tr className="border-b border-gray-100">
                            <td className="font-mono pr-4 py-2 text-amber-600">titulo</td>
                            <td className="pr-4 py-2"><span className="text-green-600">✓ Sí</span></td>
                            <td className="py-2">Título corto (máximo 40 caracteres)</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="font-mono pr-4 py-2 text-amber-600">mensaje</td>
                            <td className="pr-4 py-2"><span className="text-green-600">✓ Sí</span></td>
                            <td className="py-2">Texto de la notificación (máximo 200 caracteres)</td>
                          </tr>
                          <tr>
                            <td className="font-mono pr-4 py-2 text-gray-500">explicacion</td>
                            <td className="pr-4 py-2"><span className="text-gray-400">Opcional</span></td>
                            <td className="py-2">Explicación detallada (se muestra al abrir la perla)</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* Ejemplo de CSV */}
                    <h4 className="font-medium text-gray-700 mb-2">Ejemplo de archivo CSV:</h4>
                    <div className="bg-gray-900 text-green-400 rounded-lg p-4 font-mono text-xs overflow-x-auto">
                      <p>titulo,mensaje,explicacion</p>
                      <p>Infarto STEMI,Tiempo puerta-balón meta menor a 90 minutos,El infarto con elevación del ST requiere reperfusión urgente...</p>
                      <p>Arritmias,FA y flutter auricular son las arritmias más comunes,La fibrilación auricular aumenta 5x el riesgo de EVC...</p>
                      <p>IC con FE reducida,Tratamiento: IECA + BB + ARM + iSGLT2,La terapia cuádruple reduce mortalidad significativamente...</p>
                    </div>
                  </div>
                    
                  {/* Paso 3: Upload */}
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <h3 className="font-semibold text-gray-800 mb-4">
                      <span className="bg-amber-500 text-white rounded-full w-6 h-6 inline-flex items-center justify-center text-sm mr-2">3</span>
                      Subir Archivo
                    </h3>
                    
                    {(() => {
                      const category = quizCategories.find(c => c.module === selectedSpecialty);
                      const needsSubtema = category?.subtemas.length > 0;
                      const canUpload = selectedSpecialty && (!needsSubtema || selectedTopic);
                      
                      return (
                        <>
                          <div className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                            canUpload 
                              ? 'border-amber-300 hover:border-amber-400 bg-amber-50/50' 
                              : 'border-gray-200 bg-gray-50'
                          }`}>
                            <input
                              type="file"
                              accept=".csv"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                  setCsvFile(file);
                                }
                              }}
                              className="hidden"
                              id="csv-upload"
                              disabled={!canUpload}
                            />
                            <label
                              htmlFor="csv-upload"
                              className={`cursor-pointer ${!canUpload ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                              <Upload className={`mx-auto mb-4 ${canUpload ? 'text-amber-500' : 'text-gray-300'}`} size={40} />
                              {!canUpload ? (
                                <p className="text-gray-400">Primero selecciona módulo{needsSubtema ? ' y subtema' : ''} arriba</p>
                              ) : csvFile ? (
                                <p className="text-amber-600 font-medium">{csvFile.name}</p>
                              ) : (
                                <p className="text-gray-600">Haz clic para seleccionar un archivo CSV</p>
                              )}
                              <p className="text-gray-400 text-sm mt-1">Máximo 20 perlas por archivo</p>
                            </label>
                          </div>
                          
                          {csvFile && canUpload && (
                            <div className="mt-4 flex gap-3">
                              <button
                                onClick={() => setCsvFile(null)}
                                className="flex-1 py-3 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50"
                        >
                          Cancelar
                        </button>
                        <button
                          onClick={handleCsvUpload}
                          disabled={uploading}
                          className="flex-1 bg-amber-500 hover:bg-amber-600 text-white font-semibold py-3 rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                          {uploading ? (
                            <>
                              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                              Importando...
                            </>
                          ) : (
                            <>
                              <Upload size={18} />
                              Importar Perlas
                            </>
                          )}
                        </button>
                      </div>
                    )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Pearl Form Modal */}
      {showPearlForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-800">
                  {editingPearl ? 'Editar Perla' : 'Nueva Perla'}
                </h3>
                <button
                  onClick={() => setShowPearlForm(false)}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <X size={20} />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subtema *
                  </label>
                  <select
                    value={pearlForm.subtopic_id}
                    onChange={(e) => setPearlForm({...pearlForm, subtopic_id: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500"
                  >
                    <option value="">Selecciona un subtema</option>
                    {subtopics.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.module_name} → {s.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Título * <span className="text-gray-400">(máx 40 caracteres)</span>
                  </label>
                  <input
                    type="text"
                    value={pearlForm.title}
                    onChange={(e) => setPearlForm({...pearlForm, title: e.target.value.slice(0, 40)})}
                    maxLength={40}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500"
                    placeholder="Ej: Infarto STEMI"
                  />
                  <p className="text-xs text-gray-400 mt-1">{pearlForm.title.length}/40</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mensaje * <span className="text-gray-400">(máx 200 caracteres)</span>
                  </label>
                  <textarea
                    value={pearlForm.message}
                    onChange={(e) => setPearlForm({...pearlForm, message: e.target.value.slice(0, 200)})}
                    maxLength={200}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500"
                    placeholder="Mensaje corto que aparecerá en la notificación"
                  />
                  <p className="text-xs text-gray-400 mt-1">{pearlForm.message.length}/200</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Explicación detallada (opcional)
                  </label>
                  <textarea
                    value={pearlForm.long_body}
                    onChange={(e) => setPearlForm({...pearlForm, long_body: e.target.value})}
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500"
                    placeholder="Explicación completa que verá el usuario al abrir la perla"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tags (separados por coma)
                    </label>
                    <input
                      type="text"
                      value={pearlForm.tags}
                      onChange={(e) => setPearlForm({...pearlForm, tags: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500"
                      placeholder="cardiología, urgencias"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Dificultad
                    </label>
                    <select
                      value={pearlForm.difficulty}
                      onChange={(e) => setPearlForm({...pearlForm, difficulty: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500"
                    >
                      <option value="">Sin definir</option>
                      <option value="Básico">Básico</option>
                      <option value="Intermedio">Intermedio</option>
                      <option value="Avanzado">Avanzado</option>
                    </select>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowPearlForm(false)}
                  className="flex-1 py-3 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleCreatePearl}
                  className="flex-1 py-3 bg-amber-500 text-white font-semibold rounded-lg hover:bg-amber-600 flex items-center justify-center gap-2"
                >
                  <Save size={18} />
                  {editingPearl ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PerlasAdmin;
